import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import Panel from "./Panel";
import EmptyPanel from "./EmptyPanel";
import { APP_FILTER_TYPES } from "constants/filter";
import { FACET_PATH_DELIMITER } from "constants/filter";
import { customTypeChecker } from "utils/propType";
import { isDeepEqual } from "utils/predicates";

const getDataFromSelection = (data, selectedValue) => {
  const selectedData =
    data?.filter((dataObject) => dataObject.value === selectedValue) || [];
  return selectedData[0]?.data || null;
};

const isFinalColumn = (index, maxIndex, data) =>
  (!data || data.length === 0) && index <= maxIndex;

const Panels = ({
  className,
  columnCount = 1,
  data = [],
  loadingLevel,
  name,
  onFilterValueChange,
  panelClass,
  parentClass,
  selectedValues: initialSelectedValues = [],
}) => {
  const [selectedValues, setSelectedValues] = useState(
    initialSelectedValues.length > 0
      ? initialSelectedValues[0].split(FACET_PATH_DELIMITER)
      : [],
  );

  useEffect(() => {
    const newSelectedValues =
      initialSelectedValues.length > 0
        ? initialSelectedValues[0].split(FACET_PATH_DELIMITER)
        : [];
    if (!isDeepEqual(newSelectedValues, selectedValues)) {
      setSelectedValues(newSelectedValues);
    }
  }, [initialSelectedValues, selectedValues]);

  const getRecursiveData = useCallback((selectedValues, data) => {
    const selectedValue = selectedValues[0];
    const selectedData = data.find((item) => item.value === selectedValue);
    const newDataObject = selectedData?.data;

    return selectedValues.length > 1 && newDataObject
      ? getRecursiveData(selectedValues.slice(1), newDataObject)
      : newDataObject;
  }, []);

  const handleFilterValueChange = useCallback(
    (index) =>
      ({ values }) => {
        const newSelectedValues = selectedValues.slice(0, index);
        if (values && values.length) {
          newSelectedValues.push(values[0]);
        }
        onFilterValueChange(
          newSelectedValues.join(FACET_PATH_DELIMITER),
          newSelectedValues.length,
        );
      },
    [onFilterValueChange, selectedValues],
  );

  const buildPanel = useCallback(
    (data, level, selectedValues) => {
      const panelClassName = classnames(panelClass, `${parentClass}__panel`);
      return (
        <Panel
          name={name}
          data={data}
          onFilterValueChange={handleFilterValueChange(level)}
          selectedValues={[selectedValues]}
          filterType={APP_FILTER_TYPES.SINGLE}
          key={level}
          noHeader={level !== 0}
          className={panelClassName}
        />
      );
    },
    [handleFilterValueChange, name, panelClass, parentClass],
  );

  const buildEmptyPanel = useCallback(
    (width, level, isFinal = false) => {
      const panelClassName = classnames(panelClass, `${parentClass}__panel`);
      return (
        <EmptyPanel
          width={width}
          key={level}
          isLoading={loadingLevel === level}
          panelKey={level + 1}
          isFinal={isFinal}
          className={panelClassName}
        />
      );
    },
    [loadingLevel, panelClass, parentClass],
  );

  const buildContent = useCallback(() => {
    const maxColumnIndex = columnCount - 1;
    let contentArray = [];
    let dataArray = data;

    for (let panelIndex = 0; panelIndex <= maxColumnIndex; panelIndex++) {
      const selectedValueOnPanel = selectedValues?.[panelIndex];
      const selectedValueOnPrevPanel = selectedValues?.[panelIndex - 1];
      const hasData = dataArray && dataArray.length;

      if (
        selectedValueOnPrevPanel &&
        isFinalColumn(panelIndex, maxColumnIndex, dataArray)
      ) {
        const panelWidth = maxColumnIndex - panelIndex + 1;
        contentArray.push(buildEmptyPanel(panelWidth, panelIndex, true));
        break;
      }

      if (hasData) {
        contentArray.push(
          buildPanel(dataArray, panelIndex, selectedValueOnPanel),
        );
      } else {
        contentArray.push(buildEmptyPanel(1, panelIndex));
      }

      dataArray = getDataFromSelection(dataArray, selectedValueOnPanel);
    }

    return contentArray;
  }, [columnCount, data, selectedValues, buildPanel, buildEmptyPanel]);

  const componentClassName = classnames(className, "sal-panels");

  return <div className={componentClassName}>{buildContent()}</div>;
};

Panels.propTypes = {
  className: PropTypes.string,
  columnCount: customTypeChecker(
    (columns) => columns > 0 && columns < 5,
    "Integer between 1 and 4",
    true,
  ),
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      count: PropTypes.number,
      data: PropTypes.array,
    }),
  ).isRequired,
  loadingLevel: PropTypes.number,
  name: PropTypes.string.isRequired,
  onFilterValueChange: PropTypes.func.isRequired,
  panelClass: PropTypes.string,
  parentClass: PropTypes.string,
  selectedValues: PropTypes.arrayOf(PropTypes.string),
};

export default Panels;
